import React, { useState, useMemo } from 'react';
import { DominionLogo } from './DominionLogo';
import { LanguageSelector } from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { LogOut, Search, Download, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getRiskProfiles, deleteRiskProfile } from '../services/api';

// Mock data structure
export interface RiskProfileRecord {
  id: string;
  createdAt: Date;
  clientName: string;
  advisorName: string;
  language: 'en' | 'es' | 'pt';
  riskScore: number;
  riskProfile: string;
  allocation: Record<string, number>;
  answers: Record<string, string>;
  pdfUrl?: string;
}

interface AdminDashboardProps {
  token: string;
  onLogout: () => void;
}

// Mock data for testing
// (kept for reference only, not used at runtime)
const _mockData: RiskProfileRecord[] = [
  {
    id: '1',
    createdAt: new Date('2024-02-20T10:30:00'),
    clientName: 'John Smith',
    advisorName: 'Jane Doe',
    language: 'en',
    riskScore: 75,
    riskProfile: 'Moderate Aggressive',
    allocation: { fixedIncome: 20, equities: 70, alternatives: 10, crypto: 0 },
    answers: { q1: '25', q2: '15', q3: '15', q4: '10', q5: '15', q6: '15' },
  },
  {
    id: '2',
    createdAt: new Date('2024-02-21T14:15:00'),
    clientName: 'María García',
    advisorName: 'Carlos López',
    language: 'es',
    riskScore: 45,
    riskProfile: 'Moderado',
    allocation: { fixedIncome: 40, equities: 50, alternatives: 10, crypto: 0 },
    answers: { q1: '15', q2: '10', q3: '10', q4: '15', q5: '10', q6: '10' },
  },
  {
    id: '3',
    createdAt: new Date('2024-02-22T09:45:00'),
    clientName: 'João Silva',
    advisorName: 'Maria Santos',
    language: 'pt',
    riskScore: 95,
    riskProfile: 'Agressivo',
    allocation: { fixedIncome: 10, equities: 80, alternatives: 10, crypto: 0 },
    answers: { q1: '25', q2: '20', q3: '20', q4: '10', q5: '20', q6: '20' },
  },
  {
    id: '4',
    createdAt: new Date('2024-02-23T11:20:00'),
    clientName: 'Emma Wilson',
    advisorName: 'Jane Doe',
    language: 'en',
    riskScore: 15,
    riskProfile: 'Conservative',
    allocation: { fixedIncome: 90, equities: 10, alternatives: 0, crypto: 0 },
    answers: { q1: '10', q2: '0', q3: '0', q4: '20', q5: '0', q6: '5' },
  },
  {
    id: '5',
    createdAt: new Date('2024-02-24T16:50:00'),
    clientName: 'Pedro Martínez',
    advisorName: 'Carlos López',
    language: 'es',
    riskScore: 32,
    riskProfile: 'Moderado Conservador',
    allocation: { fixedIncome: 60, equities: 30, alternatives: 10, crypto: 0 },
    answers: { q1: '15', q2: '5', q3: '5', q4: '15', q5: '5', q6: '10' },
  },
];

export function AdminDashboard({ token, onLogout }: AdminDashboardProps) {
  const { translations: t } = useLanguage();
  const [records, setRecords] = useState<RiskProfileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfile, setFilterProfile] = useState<string>('all');
  const [filterLanguage, setFilterLanguage] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<string | null>(null);

  // Fetch records from API on mount
  React.useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        console.log('🔍 [AdminDashboard] Fetching risk profiles...');
        console.log('🔍 [AdminDashboard] Token length:', token?.length || 0);
        console.log('🔍 [AdminDashboard] Token preview:', token?.substring(0, 20) + '...');
        
        const response = await getRiskProfiles(token);
        
        if (response.success) {
          const transformedRecords: RiskProfileRecord[] = response.data.map((profile) => ({
            id: profile.id,
            createdAt: new Date(profile.createdAt),
            clientName: profile.name,
            advisorName: profile.email,
            language: profile.language as 'en' | 'es' | 'pt',
            riskScore: profile.totalScore,
            riskProfile: profile.riskLevel,
            allocation: profile.allocation,
            answers: profile.answers,
          }));
          
          setRecords(transformedRecords);
          console.log(`✅ [AdminDashboard] Loaded ${transformedRecords.length} risk profiles`);
        } else {
          console.error('❌ [AdminDashboard] Response not successful:', response);
          toast.error('Failed to load risk profiles');
        }
      } catch (error) {
        console.error('❌ [AdminDashboard] Error fetching risk profiles:', error);
        if (error instanceof Error && error.message.includes('Unauthorized')) {
          toast.error('Session expired. Please login again.');
          onLogout();
        } else {
          toast.error('Failed to load risk profiles');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [token, onLogout]);

  // Filter and search logic
  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const matchesSearch =
        record.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.advisorName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesProfile =
        filterProfile === 'all' || record.riskProfile === filterProfile;

      const matchesLanguage =
        filterLanguage === 'all' || record.language === filterLanguage;

      return matchesSearch && matchesProfile && matchesLanguage;
    });
  }, [records, searchTerm, filterProfile, filterLanguage]);

  // Get unique profiles from records
  const uniqueProfiles = useMemo(() => {
    return Array.from(new Set(records.map((r) => r.riskProfile)));
  }, [records]);

  const handleDelete = (id: string) => {
    setRecordToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (recordToDelete) {
      try {
        const response = await deleteRiskProfile(recordToDelete, token);
        if (response.success) {
          toast.success(t.admin.dashboard.deleteSuccess);
          // Remove the deleted record from the state
          setRecords(records.filter((record) => record.id !== recordToDelete));
        } else {
          toast.error('Failed to delete risk profile');
        }
      } catch (error) {
        console.error('Error deleting risk profile:', error);
        toast.error('Failed to delete risk profile');
      } finally {
        setDeleteDialogOpen(false);
        setRecordToDelete(null);
      }
    }
  };

  // removed handleView — eye button no longer used

  const handleDownload = async (record: RiskProfileRecord) => {
    try {
      const { getPdfUrl } = await import('../services/api');
      const response = await getPdfUrl(record.id, token);
      
      if (response.success && response.url) {
        // Open PDF in new window
        window.open(response.url, '_blank');
        toast.success(`Opening PDF for ${record.clientName}`);
      } else {
        toast.error('PDF not found for this profile');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getLanguageLabel = (lang: string) => {
    switch (lang) {
      case 'en':
        return 'English';
      case 'es':
        return 'Español';
      case 'pt':
        return 'Português';
      default:
        return lang;
    }
  };

  // Skeleton rows for loading state
  const SkeletonRow = () => (
    <TableRow className="border-[#27272a]">
      {[...Array(7)].map((_, i) => (
        <TableCell key={i} className={i === 0 ? 'pl-6' : i === 6 ? 'pr-6' : ''}>
          <div className="h-4 bg-[#27272a] rounded animate-pulse" style={{ width: i === 4 ? '120px' : i === 6 ? '80px' : '80px' }} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <div className="min-h-screen bg-[#0d0d12] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#90d977]/20 to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#90d977]/10 to-transparent blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <DominionLogo className="h-6 md:h-8 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="bg-transparent border-[#27272a] text-[#a1a1aa] hover:bg-[#1a1b25] hover:text-white cursor-pointer"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t.admin.dashboard.logoutButton}
            </Button>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div className="mb-8">
          <h1 className="text-3xl text-white mb-2">
            {t.admin.dashboard.title}
          </h1>
          <p className="text-[#a1a1aa]">{t.admin.dashboard.subtitle}</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-[#1a1b25] rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a1a1aa]" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t.admin.dashboard.searchPlaceholder}
                className="pl-10 bg-[#0d0d12] border-[#27272a] text-white placeholder:text-[#71717a] focus:border-[#90d977] focus:ring-[#90d977]"
              />
            </div>

            {/* Profile Filter */}
            <div>
              <Select value={filterProfile} onValueChange={setFilterProfile}>
                <SelectTrigger className="bg-[#0d0d12] border-[#27272a] text-white focus:border-[#90d977] focus:ring-[#90d977]">
                  <SelectValue placeholder={t.admin.dashboard.filterByProfile} />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1b25] border-[#27272a]">
                  <SelectItem value="all" className="text-white focus:bg-[#0d0d12] focus:text-white">
                    {t.admin.dashboard.allProfiles}
                  </SelectItem>
                  {uniqueProfiles.map((profile) => (
                    <SelectItem
                      key={profile}
                      value={profile}
                      className="text-white focus:bg-[#0d0d12] focus:text-white"
                    >
                      {profile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Language Filter */}
            <div>
              <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                <SelectTrigger className="bg-[#0d0d12] border-[#27272a] text-white focus:border-[#90d977] focus:ring-[#90d977]">
                  <SelectValue placeholder={t.admin.dashboard.filterByLanguage} />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1b25] border-[#27272a]">
                  <SelectItem value="all" className="text-white focus:bg-[#0d0d12] focus:text-white">
                    {t.admin.dashboard.allLanguages}
                  </SelectItem>
                  <SelectItem value="en" className="text-white focus:bg-[#0d0d12] focus:text-white">
                    English
                  </SelectItem>
                  <SelectItem value="es" className="text-white focus:bg-[#0d0d12] focus:text-white">
                    Español
                  </SelectItem>
                  <SelectItem value="pt" className="text-white focus:bg-[#0d0d12] focus:text-white">
                    Português
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-[#a1a1aa]">
            {t.admin.dashboard.totalResults.replace('{count}', filteredRecords.length.toString())}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#1a1b25] rounded-lg overflow-hidden">
          {loading ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#27272a]">
                    <TableHead className="text-[#a1a1aa] pl-6">Client</TableHead>
                    <TableHead className="text-[#a1a1aa]">Profile</TableHead>
                    <TableHead className="text-[#a1a1aa]">Score</TableHead>
                    <TableHead className="text-[#a1a1aa]">Date</TableHead>
                    <TableHead className="text-[#a1a1aa]">Advisor</TableHead>
                    <TableHead className="text-[#a1a1aa]">Language</TableHead>
                    <TableHead className="text-[#a1a1aa] text-right pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
                </TableBody>
              </Table>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <FileText className="h-16 w-16 text-[#a1a1aa] mb-4" />
              <h3 className="text-xl text-white mb-2">{t.admin.dashboard.noResults}</h3>
              <p className="text-[#a1a1aa] text-sm">{t.admin.dashboard.noResultsDescription}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#27272a] hover:bg-transparent">
                    <TableHead className="text-[#a1a1aa] pl-6">{t.admin.dashboard.tableHeaders.client}</TableHead>
                    <TableHead className="text-[#a1a1aa]">{t.admin.dashboard.tableHeaders.profile}</TableHead>
                    <TableHead className="text-[#a1a1aa]">{t.admin.dashboard.tableHeaders.score}</TableHead>
                    <TableHead className="text-[#a1a1aa]">{t.admin.dashboard.tableHeaders.date}</TableHead>
                    <TableHead className="text-[#a1a1aa]">{t.admin.dashboard.tableHeaders.advisor}</TableHead>
                    <TableHead className="text-[#a1a1aa]">{t.admin.dashboard.tableHeaders.language}</TableHead>
                    <TableHead className="text-[#a1a1aa] text-right pr-6">{t.admin.dashboard.tableHeaders.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id} className="border-[#27272a] hover:bg-[#0d0d12]/50">
                      <TableCell className="text-white pl-6">{record.clientName}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-[#90d977]/10 text-[#90d977] border border-[#90d977]/20">
                          {record.riskProfile}
                        </span>
                      </TableCell>
                      <TableCell className="text-white">{record.riskScore}</TableCell>
                      <TableCell className="text-white">{formatDate(record.createdAt)}</TableCell>
                      <TableCell className="text-white">{record.advisorName}</TableCell>
                      <TableCell className="text-white">{getLanguageLabel(record.language)}</TableCell>
                      <TableCell className="pr-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => handleDownload(record)}
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-[#a1a1aa] hover:text-white hover:bg-[#0d0d12] cursor-pointer"
                            title={t.admin.dashboard.actions.download}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(record.id)}
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            title={t.admin.dashboard.actions.delete}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1a1b25] border-[#27272a]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {t.admin.dashboard.actions.delete}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              {t.admin.dashboard.confirmDelete}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#27272a] text-[#a1a1aa] hover:bg-[#0d0d12] hover:text-white">
              {t.admin.dashboard.deleteCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t.admin.dashboard.actions.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}